// This file is for checking out purposes / new member registration workflow, not to view the subscription.
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { subscriptionApi } from '../api';
import type { SubscriptionPlan, CheckoutSessionRequest } from '../types/subscription';
import 'react-toastify/dist/ReactToastify.css';
import { useFacility } from '../hooks/useFacility';

const NewSubscription: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, currentUser } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const facilityId = searchParams.get('facilityId') || '';
  const businessId = searchParams.get('businessId') || localStorage.getItem('businessId') || '';
  const { facility, loading: facilityLoading, error: facilityError } = useFacility(facilityId, businessId);

  useEffect(() => {
    if (facility && facility.isPPU) {
      // No plan selection or checkout for PPU
      setSelectedPlan(null);
      setLoading(false);
      return;
    }
    const fetchPlan = async () => {
      const planId = searchParams.get('planId');
      const facilityId = searchParams.get('facilityId');
      const inviteCodeId = sessionStorage.getItem('invite_code_id');
      
      if (!planId || !facilityId || !businessId) {
        setError('Missing required parameters');
        return;
      }

      if (!inviteCodeId || inviteCodeId === 'null') {
        setError('Invalid invite code');
        navigate('/member/register');
        return;
      }

      try {
        setLoading(true);
        const plans = await subscriptionApi.getSubscriptionPlans(businessId, facilityId);
        const plan = plans.find(p => p.priceId === planId);
        if (!plan) {
          setError('Selected plan not found');
          return;
        }
        setSelectedPlan(plan);
      } catch (error) {
        console.error('Error fetching plan:', error);
        setError('Failed to load plan details');
      } finally {
        setLoading(false);
      }
    };
    fetchPlan();
  }, [searchParams, navigate, facility, businessId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const facilityId = searchParams.get('facilityId');
    const inviteCodeId = sessionStorage.getItem('invite_code_id');

    if (!selectedPlan || !currentUser || !facilityId || !businessId || !inviteCodeId || inviteCodeId === 'null') {
      setError('Missing required data');
      return;
    }

    try {
      setLoading(true);
      const checkoutRequest: CheckoutSessionRequest = {
        price_id: selectedPlan.priceId,
        member_data: {
          uid: currentUser.uid,
          email: currentUser.email || '',
          facility_id: facilityId,
          business_id: businessId,
          invite_code_id: inviteCodeId
        },
        success_url: `${window.location.origin}/member/register?success=true&session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${window.location.origin}/member/plans?facilityId=${facilityId}&businessId=${businessId}`,
        metadata: {
          member_uid: currentUser.uid,
          business_id: businessId
        }
      };

      // Store credentials for post-checkout authentication
      sessionStorage.setItem('userEmail', currentUser.email || '');
      sessionStorage.setItem('facilityId', facilityId);
      sessionStorage.setItem('businessId', businessId);
      localStorage.setItem('facilityId', facilityId);
      localStorage.setItem('businessId', businessId);

      const response = await subscriptionApi.createCheckoutSession(checkoutRequest);
      if (response.url) {
        window.location.href = response.url;
      } else {
        setError('No checkout URL received');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      setError('Failed to create checkout session. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (facilityLoading) {
    return (
      <div className="max-w-2xl mx-auto p-4">
        <div className="flex justify-center items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      </div>
    );
  }
  if (facilityError) {
    return (
      <div className="max-w-2xl mx-auto p-4">
        <div className="bg-red-50 border border-red-400 text-red-700 px-4 py-3 rounded relative">
          <strong className="font-bold">Error: </strong>
          <span className="block sm:inline">{facilityError}</span>
        </div>
      </div>
    );
  }
  if (facility && facility.isPPU) {
    return (
      <div className="max-w-2xl mx-auto p-4">
        <div className="bg-green-50 border border-green-400 text-green-700 px-4 py-3 rounded relative">
          <strong className="font-bold">Registration Complete!</strong>
          <span className="block sm:inline"> You have registered for a Pay-Per-Use facility. Please check your email for verification and next steps.</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto p-4">
        <div className="bg-red-50 border border-red-400 text-red-700 px-4 py-3 rounded relative">
          <strong className="font-bold">Error: </strong>
          <span className="block sm:inline">{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-4">
      <h1 className="text-2xl font-bold mb-4">Complete Your Subscription</h1>
      {loading ? (
        <div className="flex justify-center items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      ) : selectedPlan ? (
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-4">{selectedPlan.name}</h2>
          <p className="text-2xl font-bold mb-6">
            {new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: selectedPlan.currency || 'USD'
            }).format(selectedPlan.amount / 100)} / {selectedPlan.interval}
          </p>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Processing...' : 'Continue to Payment'}
          </button>
        </div>
      ) : (
        <p>Loading plan details...</p>
      )}
    </div>
  );
};

export { NewSubscription }; 